import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    type DocumentSnapshot,
    type QuerySnapshot,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import type { CollaboratorPrivate } from '../Models';
import { CollaboratorPrivateSchema } from '../Models';

// ================================================
// COLABORADOR PRIVATE SERVICE
// Fonte única da verdade para dados de colaboradores
// ================================================

const COLLECTION_NAME = 'collaborators_private';

// ================================================
// HELPERS DE CONVERSÃO
// ================================================

/**
 * Converte dados do Firestore para o modelo TypeScript
 */
const firestoreToCollaborator = (doc: DocumentSnapshot): CollaboratorPrivate | null => {
    if (!doc.exists()) return null;

    const data = doc.data();

    // Converter Timestamps para Date objects
    const processedData = {
        ...data,
        id: doc.id,
        _metadata: {
            ...data._metadata,
            createdAt: data._metadata?.createdAt?.toDate?.() || null,
            updatedAt: data._metadata?.updatedAt?.toDate?.() || null,
        },
    };

    try {
        return CollaboratorPrivateSchema.parse(processedData);
    } catch (error) {
        console.error('Erro ao validar dados do colaborador:', error);
        return processedData as CollaboratorPrivate;
    }
};

/**
 * Converte modelo TypeScript para dados do Firestore
 */
const collaboratorToFirestore = (collaborator: Partial<CollaboratorPrivate>) => {
    const { id, _metadata, ...data } = collaborator;

    // Converter Date objects para Timestamps
    const processedData = {
        ...data,
        _metadata: {
            ..._metadata,
            updatedAt: Timestamp.now(),
            version: (_metadata?.version || 0) + 1,
            createdAt: _metadata?.createdAt || Timestamp.now(),
        },
    };

    // Remove campos undefined (Firestore não aceita)
    return JSON.parse(JSON.stringify(processedData));
};

// ================================================
// CRUD OPERATIONS
// ================================================

/**
 * Busca colaborador por ID
 */
export const getCollaboratorById = async (id: string): Promise<CollaboratorPrivate | null> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);
        return firestoreToCollaborator(docSnap);
    } catch (error) {
        console.error('Erro ao buscar colaborador:', error);
        throw new Error('Erro ao buscar colaborador');
    }
};

/**
 * Busca colaborador por email
 */
export const getCollaboratorByEmail = async (email: string): Promise<CollaboratorPrivate | null> => {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where('email', '==', email),
            limit(1)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) return null;

        const doc = querySnapshot.docs[0];
        return firestoreToCollaborator(doc);
    } catch (error) {
        console.error('Erro ao buscar colaborador por email:', error);
        throw new Error('Erro ao buscar colaborador por email');
    }
};

/**
 * Lista todos os colaboradores com paginação
 */
export const getCollaborators = async (options?: {
    limitCount?: number;
    orderByField?: keyof CollaboratorPrivate;
    orderDirection?: 'asc' | 'desc';
}): Promise<CollaboratorPrivate[]> => {
    try {
        const constraints = [];

        if (options?.orderByField) {
            constraints.push(orderBy(options.orderByField, options.orderDirection || 'asc'));
        }

        if (options?.limitCount) {
            constraints.push(limit(options.limitCount));
        }

        const q = query(collection(db, COLLECTION_NAME), ...constraints);
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs
            .map(doc => firestoreToCollaborator(doc))
            .filter((collaborator): collaborator is CollaboratorPrivate => collaborator !== null);
    } catch (error) {
        console.error('Erro ao listar colaboradores:', error);
        throw new Error('Erro ao listar colaboradores');
    }
};

/**
 * Cria novo colaborador
 */
export const createCollaborator = async (collaboratorData: Omit<CollaboratorPrivate, 'id' | '_metadata'>): Promise<string> => {
    try {
        // Validar dados antes de salvar
        const validatedData = CollaboratorPrivateSchema.omit({ id: true, _metadata: true }).parse(collaboratorData);

        const firestoreData = collaboratorToFirestore({
            ...validatedData,
            _metadata: {
                createdAt: new Date(),
                updatedAt: new Date(),
                version: 1,
            },
        } as Partial<CollaboratorPrivate>);

        const docRef = await addDoc(collection(db, COLLECTION_NAME), firestoreData);
        return docRef.id;
    } catch (error) {
        console.error('Erro ao criar colaborador:', error);
        throw new Error('Erro ao criar colaborador');
    }
};

/**
 * Atualiza colaborador existente
 */
export const updateCollaborator = async (id: string, updates: Partial<CollaboratorPrivate>): Promise<void> => {
    try {
        // Buscar dados atuais para preservar metadados
        const current = await getCollaboratorById(id);
        if (!current) {
            throw new Error('Colaborador não encontrado');
        }

        // Merge com dados atuais
        const updatedData = {
            ...current,
            ...updates,
            id, // Garante que o ID não mude
        };

        // Validar dados antes de salvar
        const validatedData = CollaboratorPrivateSchema.parse(updatedData);
        const firestoreData = collaboratorToFirestore(validatedData);

        const docRef = doc(db, COLLECTION_NAME, id);
        await updateDoc(docRef, firestoreData);
    } catch (error) {
        console.error('Erro ao atualizar colaborador:', error);
        throw new Error('Erro ao atualizar colaborador');
    }
};

/**
 * Atualização parcial (patch) - apenas campos fornecidos
 */
export const patchCollaborator = async (id: string, updates: Partial<CollaboratorPrivate>): Promise<void> => {
    try {
        const firestoreData = collaboratorToFirestore(updates);

        const docRef = doc(db, COLLECTION_NAME, id);
        await updateDoc(docRef, firestoreData);
    } catch (error) {
        console.error('Erro ao fazer patch do colaborador:', error);
        throw new Error('Erro ao fazer patch do colaborador');
    }
};

/**
 * Remove colaborador (soft delete - marca como inativo)
 */
export const deactivateCollaborator = async (id: string): Promise<void> => {
    try {
        await patchCollaborator(id, {
            status: 'inactive',
            endDate: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Erro ao desativar colaborador:', error);
        throw new Error('Erro ao desativar colaborador');
    }
};

/**
 * Remove colaborador permanentemente (hard delete)
 */
export const deleteCollaborator = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Erro ao deletar colaborador:', error);
        throw new Error('Erro ao deletar colaborador');
    }
};

// ================================================
// QUERIES ESPECÍFICAS
// ================================================

/**
 * Busca colaboradores por departamento
 */
export const getCollaboratorsByDepartment = async (departmentId: string): Promise<CollaboratorPrivate[]> => {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where('departmentId', '==', departmentId),
            orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs
            .map(doc => firestoreToCollaborator(doc))
            .filter((collaborator): collaborator is CollaboratorPrivate => collaborator !== null);
    } catch (error) {
        console.error('Erro ao buscar colaboradores por departamento:', error);
        throw new Error('Erro ao buscar colaboradores por departamento');
    }
};

/**
 * Busca colaboradores por responsável
 */
export const getCollaboratorsByResponsible = async (responsibleId: string): Promise<CollaboratorPrivate[]> => {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where('responsibleId', '==', responsibleId),
            orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs
            .map(doc => firestoreToCollaborator(doc))
            .filter((collaborator): collaborator is CollaboratorPrivate => collaborator !== null);
    } catch (error) {
        console.error('Erro ao buscar colaboradores por responsável:', error);
        throw new Error('Erro ao buscar colaboradores por responsável');
    }
};

/**
 * Busca colaboradores ativos
 */
export const getActiveCollaborators = async (): Promise<CollaboratorPrivate[]> => {
    try {
        const q = query(
            collection(db, COLLECTION_NAME),
            where('status', '!=', 'inactive'),
            orderBy('status'),
            orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs
            .map(doc => firestoreToCollaborator(doc))
            .filter((collaborator): collaborator is CollaboratorPrivate => collaborator !== null);
    } catch (error) {
        console.error('Erro ao buscar colaboradores ativos:', error);
        throw new Error('Erro ao buscar colaboradores ativos');
    }
};

// ================================================
// HEALTH CHECK
// ================================================

/**
 * Verifica a saúde da coleção e retorna estatísticas
 */
export const getCollectionHealth = async (): Promise<{
    totalDocuments: number;
    activeCollaborators: number;
    lastUpdate?: Date;
}> => {
    try {
        const [allDocs, activeDocs] = await Promise.all([
            getDocs(collection(db, COLLECTION_NAME)),
            getDocs(query(
                collection(db, COLLECTION_NAME),
                where('status', '!=', 'inactive')
            ))
        ]);

        let lastUpdate: Date | undefined;
        allDocs.docs.forEach(doc => {
            const data = doc.data();
            const updatedAt = data._metadata?.updatedAt?.toDate?.();
            if (updatedAt && (!lastUpdate || updatedAt > lastUpdate)) {
                lastUpdate = updatedAt;
            }
        });

        return {
            totalDocuments: allDocs.size,
            activeCollaborators: activeDocs.size,
            lastUpdate,
        };
    } catch (error) {
        console.error('Erro ao verificar saúde da coleção:', error);
        throw new Error('Erro ao verificar saúde da coleção');
    }
}; 